import type {Dispatch, MutableRefObject, SetStateAction} from "react";
import {useLayoutEffect, useRef, useState} from "react";
import {shallow} from "zustand/vanilla/shallow";

export const findScrollableParents = (root: HTMLElement) => {
  const scrollableParents: (HTMLElement | Window)[] = [window];
  const inner = (node: HTMLElement | null) => {
    if (!node) return;

    const style = getComputedStyle(node);
    const overflowRegex = /(auto|scroll|overlay)/;

    if (overflowRegex.test(style.overflowY + style.overflowX)) {
      scrollableParents.push(node);
    }

    inner(node.offsetParent as HTMLElement);
  };
  inner(root);
  return scrollableParents;
};

type Bounds = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  height: number;
  width: number;
};

class IntervalManager {
  subs: Set<() => void> = new Set();
  intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private interval = 1000) {}

  subscribe(cb: () => void) {
    this.subs.add(cb);
    if (!this.intervalId) {
      this.intervalId = setInterval(() => {
        this.subs.forEach((fn) => fn());
      }, this.interval);
    }
    return () => {
      this.subs.delete(cb);
      if (!this.subs.size && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    };
  }
}

const intervalManager = new IntervalManager(1000);

const subscribePositionChange = (
  node: HTMLElement,
  setPos: Dispatch<SetStateAction<Bounds | null>>
) => {
  const updatePos = () => {
    const rect = node.getBoundingClientRect();
    const left = rect.left + window.pageXOffset;
    const top = rect.top + window.pageYOffset;
    const right = left + rect.width;
    const bottom = top + rect.height;
    const height = rect.height;
    const width = rect.width;

    setPos((prev) => {
      if (!prev) return {left, top, right, bottom, height, width};
      if (
        prev.left !== left ||
        prev.top !== top ||
        prev.right !== right ||
        prev.bottom !== bottom
      ) {
        return {left, top, right, bottom, height, width};
      }
      return prev;
    });
  };
  const unsubInterval = intervalManager.subscribe(updatePos);
  updatePos();
  window.addEventListener("resize", updatePos);
  const parents = findScrollableParents(node);
  parents.forEach((parent) => parent.addEventListener("scroll", updatePos));

  return () => {
    window.removeEventListener("resize", updatePos);
    parents.forEach((parent) => parent.removeEventListener("scroll", updatePos));
    unsubInterval();
  };
};

export const useDSPosition = () => {
  const refs = useRef<{
    unsubscribeCb: null | (() => void);
    cbRef: (node: HTMLElement | null) => void;
  }>();
  const [pos, setPos] = useState<null | Bounds>(null);
  if (!refs.current)
    refs.current = {
      unsubscribeCb: null,
      cbRef: (node) => {
        if (refs.current!.unsubscribeCb) refs.current!.unsubscribeCb();
        if (!node) return;
        const unsub = subscribePositionChange(node, setPos);
        refs.current!.unsubscribeCb = () => {
          unsub();
          refs.current!.unsubscribeCb = null;
        };
      },
    };
  return [refs.current.cbRef, pos] as const;
};

export const useDSPositionWithNode = (node: HTMLElement | null | undefined) => {
  const [pos, setPos] = useState<null | Bounds>(null);
  useLayoutEffect(() => {
    if (node) return subscribePositionChange(node, setPos);
  }, [node]);
  return pos;
};

export const useDSDimensions = () => {
  const [dims, setDims] = useState<null | {width: number; height: number}>(null);
  const cbRef = useDSDimensionCallback(setDims);
  return [cbRef, dims] as const;
};

type Rect = {top: number; left: number; width: number; height: number};

// minimizes the number of renders, while also allowing to react to callbacks that change during dimension updates
export const useDSComputeWithDimensions = <T>(cb: (rect: null | Rect) => T) => {
  const [, updateFn] = useState(0);
  const refs = useRef<{lastResult: T; lastRect: null | Rect; cb: any}>(null as any);
  if (!refs.current) {
    refs.current = {lastResult: undefined as T, lastRect: null, cb};
  }
  refs.current.cb = cb;

  const nodeCbRef = useDSDimensionCallback((dims) => {
    const nextResult = refs.current.cb(dims);
    refs.current.lastRect = dims;
    if (nextResult !== refs.current.lastResult) {
      updateFn((v) => v + 1);
    }
  });
  const result = cb(refs.current.lastRect);
  refs.current.lastResult = result;
  return [nodeCbRef, result] as const;
};

export const useDSDimensionCallback = (cb: (rect: Rect) => void) => {
  const refs = useRef<{
    unsubscribeCb: null | (() => void);
    cbRef: (node: HTMLElement | null) => void;
  }>();
  const cbRef = useRef(cb);
  cbRef.current = cb;
  if (!refs.current) {
    let lastRect: null | Rect = null;
    refs.current = {
      unsubscribeCb: null,
      cbRef: (node) => {
        if (refs.current!.unsubscribeCb) refs.current!.unsubscribeCb();
        if (!node) return;
        const update = () => {
          const {top, left, width, height} = node.getBoundingClientRect();
          const nextRect = {top, left, width, height};
          if (!shallow(nextRect, lastRect)) {
            cbRef.current(nextRect);
            lastRect = nextRect;
          }
        };
        const ro = new ResizeObserver(update);
        ro.observe(node);
        update();
        refs.current!.unsubscribeCb = () => {
          refs.current!.unsubscribeCb = null;
          ro.disconnect();
        };
      },
    };
  }

  return refs.current.cbRef;
};

export const useDSDimensionCallbackWithStableRef = (
  nodeRef: MutableRefObject<HTMLElement | null>,
  cb: (rect: Rect) => void
) => {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useLayoutEffect(() => {
    let lastRect: null | Rect = null;
    const update = () => {
      const {top, left, width, height} = nodeRef.current!.getBoundingClientRect();
      const nextRect = {top, left, width, height};
      if (!shallow(nextRect, lastRect)) {
        cbRef.current(nextRect);
        lastRect = nextRect;
      }
    };
    const ro = new ResizeObserver(update);
    ro.observe(nodeRef.current!);
    update();
    return () => {
      ro.disconnect();
    };
  }, [nodeRef]);
};
